import { Fragment } from 'react'

import { ChevronRight } from 'lucide-react'

import { Skeleton } from '~/components/component-library'
import { Button } from '~/components/component-library/Button'
// Not from the Button barrel: tests that mock the barrel would drop buttonVariants
import { buttonVariants } from '~/components/component-library/Button/styles'
import { cn } from '~/utils/tailwind-utils'

import type {
  BreadcrumbItem as BreadcrumbItemType,
  BreadcrumbProps,
} from '~/components/component-library/Breadcrumb/types'

export type {
  BreadcrumbProps,
  BreadcrumbItem,
} from '~/components/component-library/Breadcrumb/types'

// The current crumb and skeletons aren't controls, so they borrow the button
// look on a span instead of rendering an inert Button
const crumbClassName = cn(
  buttonVariants({ variant: 'tertiary', size: 'small' }),
  'pointer-events-none',
)

const BreadcrumbItem = ({
  label,
  href,
  isCurrent = false,
}: BreadcrumbItemType & { isCurrent?: boolean }) => {
  const labelSpan = <span className="max-w-32 truncate">{label}</span>

  if (isCurrent || !href) {
    return (
      <span
        className={crumbClassName}
        aria-current={isCurrent ? 'page' : undefined}
      >
        {labelSpan}
      </span>
    )
  }

  return (
    <Button variant="tertiary" size="small" href={{ path: href }}>
      {labelSpan}
    </Button>
  )
}

const BreadcrumbSeparator = () => (
  <ChevronRight className="h-4 w-4 text-secondary" />
)

const BreadcrumbSkeleton = () => (
  <nav aria-label="Breadcrumb" className="flex items-center">
    <span className={crumbClassName}>
      <Skeleton size="md" length={4} />
    </span>
    <BreadcrumbSeparator />
    <span className={crumbClassName}>
      <Skeleton size="md" length={6} />
    </span>
  </nav>
)

export const Breadcrumb = ({ items, isLoading }: BreadcrumbProps) => {
  if (isLoading) return <BreadcrumbSkeleton />

  return (
    <nav aria-label="Breadcrumb" className="flex items-center">
      {items.map((item, index) => {
        const isLast = index === items.length - 1
        return (
          <Fragment key={`${item.label}-${index}`}>
            {index > 0 && <BreadcrumbSeparator />}
            <BreadcrumbItem {...item} isCurrent={isLast} />
          </Fragment>
        )
      })}
    </nav>
  )
}
